/**
 * Marketing attribution — how this visitor found us.
 *
 * GA4 already stitches the cross-domain journey for ITS OWN reports. This is a
 * different job: getting the campaign onto our own records so "which campaign
 * produced this enterprise lead?" is a SQL query against Wicklander's database
 * rather than a question only Google can answer.
 *
 * FIRST touch, never last. A visitor who arrives via LinkedIn, leaves, and comes
 * back through a retargeting ad stays credited to LinkedIn — otherwise the last
 * click quietly eats the credit for every conversion it merely finished.
 *
 * Storage is gated on analytics consent, same signal as the tags. A visitor who
 * declines is never given a visitor id and never persisted.
 */

import API from '../api/client';

import { readStoredConsent } from './consent';

export const ATTRIBUTION_STORAGE_KEY = 'wz_attribution';

/** Set once the server has confirmed it holds this visitor's touch. */
const SYNCED_KEY = 'wz_attribution_synced';

/** The landing touch, held in the tab until consent decides its fate. */
const PENDING_TOUCH_KEY = 'wz_pending_touch';

/** The param w-z.com uses to hand its visitor id across the domain boundary. */
export const VISITOR_ID_PARAM = 'wz_vid';

export interface Attribution {
  visitor_id: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  referrer?: string;
  landing_page?: string;
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

function newVisitorId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * Read a touch off a landing URL. Always returns one: an untagged visit with no
 * referrer is a real, attributable touch ("direct"), and dropping it would make
 * every report silently exclude direct traffic.
 */
export function parseAttribution(href: string, referrer: string): Attribution {
  const url = new URL(href);
  const params = url.searchParams;

  const touch: Attribution = {
    // A visitor id handed over by the marketing site wins: it's the same human,
    // and minting a fresh id here would split their journey in two and strand
    // the campaign credit on the marketing-site half.
    visitor_id: params.get(VISITOR_ID_PARAM) || readFirstTouch()?.visitor_id || newVisitorId(),
    landing_page: url.pathname,
  };

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) touch[key] = value;
  }

  const gclid = params.get('gclid');
  if (gclid) touch.gclid = gclid;
  if (referrer) touch.referrer = referrer;

  return touch;
}

/** The stored first touch, or null if consent was declined or none is recorded. */
export function readFirstTouch(): Attribution | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

/**
 * Persist the touch — but only once, and only with consent.
 *
 * Consent is checked at write time rather than at capture time on purpose: the
 * referrer and landing URL exist only on the page the visitor first arrived at,
 * which is the same page the banner is still sitting unanswered on. Callers
 * capture eagerly and call this again when the banner is answered, so accepting
 * doesn't cost the visitor the very touch that brought them here.
 */
export function rememberFirstTouch(touch: Attribution): void {
  if (readStoredConsent() !== true) return;
  if (typeof localStorage === 'undefined') return;
  if (readFirstTouch()) return;

  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(touch));
  } catch {
    /* storage disabled or full — attribution is best-effort, never fatal */
  }
}

/**
 * Capture the landing touch, and persist it as soon as we're allowed to.
 *
 * Call this on boot AND again whenever consent is answered. The two-step exists
 * because of an ordering problem that would otherwise lose the campaign: the
 * visitor lands with `?utm_source=linkedin` on a page where the consent banner
 * is still sitting unanswered. We may not write to localStorage yet — but by the
 * time they accept, they may have navigated on, and the UTMs and referrer are
 * gone from the URL forever.
 *
 * So the raw touch is parked in sessionStorage (which is not consented storage
 * in any meaningful sense — it dies with the tab and never leaves the browser)
 * and promoted to the real, durable record the moment consent lands.
 */
export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return;

  let pending = readPendingTouch();
  if (!pending) {
    pending = parseAttribution(window.location.href, document.referrer);
    try {
      sessionStorage.setItem(PENDING_TOUCH_KEY, JSON.stringify(pending));
    } catch {
      /* storage disabled — fall through and try to persist directly */
    }
  }

  rememberFirstTouch(pending);
}

function readPendingTouch(): Attribution | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PENDING_TOUCH_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

/**
 * Hand the stored touch to the API so it lands on the User row.
 *
 * Call once the visitor is authenticated — this can't ride the sign-up request,
 * because Users are created server-side from a JWT inside the auth filter, on
 * whatever authenticated call happens to arrive first. There is no sign-up body
 * to carry UTMs.
 *
 * The server is first-touch-wins and idempotent, so a duplicate POST is
 * harmless; the synced flag only exists to avoid a request per page load. It is
 * set AFTER the server confirms — marking it up front would strand the visitor's
 * campaign for good on any transient failure.
 */
export async function syncFirstTouch(): Promise<void> {
  const touch = readFirstTouch();
  if (!touch) return;
  if (typeof localStorage !== 'undefined' && localStorage.getItem(SYNCED_KEY)) return;

  try {
    await API.post('/attributions', { attribution: touch });
    localStorage.setItem(SYNCED_KEY, '1');
  } catch {
    /* leave it unsynced — the next page load tries again */
  }
}

/** Withdrawing consent must erase the touch, not just stop sending it. */
export function clearFirstTouch(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  localStorage.removeItem(SYNCED_KEY);
  sessionStorage.removeItem(PENDING_TOUCH_KEY);
}
